import { type DataViewWidgetProps, renderUniLit } from '@blocksuite/data-view';
import {
  type CompactLeadingViewItem,
  widgetPresets,
} from '@blocksuite/data-view/widget-presets';
import { html, nothing, type TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';

import {
  databaseChromePrimaryRowStyles,
  databaseHeaderActionsStyles,
  databaseHeaderAreaStyles,
  databaseHeaderBarStyles,
  databaseHeaderContainerStyles,
  databaseHeaderStickyStyles,
  databaseTitleRowStyles,
  databaseToolbarRowStyles,
  databaseTrailingActionsStyles,
  databaseViewBarContainerStyles,
} from './database-block-styles';

export type DatabaseDataViewChromeSurface =
  | 'canvas'
  | 'embedded'
  | 'fullscreen'
  | 'page';

type DatabaseViewBarOptions = {
  activateCurrentViewOnClick?: boolean;
  compact?: boolean;
  compactLeadingItem?: CompactLeadingViewItem;
  onChangeView?: (viewId: string) => void;
  openOptionsOnActiveViewClick?: boolean;
  selectedViewId?: string | null;
};

type DatabaseDataViewChromeOptions = {
  props: DataViewWidgetProps;
  surface: DatabaseDataViewChromeSurface;
  inlineBoundary?: TemplateResult | typeof nothing;
  quickSettings?: TemplateResult | typeof nothing;
  showQuickSettings?: boolean;
  showTitleRow?: boolean;
  showToolbarRow?: boolean;
  showTools?: boolean;
  showViewBar?: boolean;
  sticky?: boolean;
  titleRow?: TemplateResult | typeof nothing;
  trailingActions?: TemplateResult | typeof nothing;
  toolbarClass?: string;
  tools?: TemplateResult | typeof nothing;
  viewBarClass?: string;
  viewBarOptions?: DatabaseViewBarOptions;
  wrapContainer?: boolean;
};

export function renderDatabaseDataViewChrome({
  props,
  surface,
  inlineBoundary = nothing,
  quickSettings = nothing,
  showQuickSettings = true,
  showTitleRow = true,
  showToolbarRow = true,
  showTools = true,
  showViewBar = true,
  sticky = false,
  titleRow = nothing,
  trailingActions = nothing,
  toolbarClass,
  tools = nothing,
  viewBarClass,
  viewBarOptions,
  wrapContainer = true,
}: DatabaseDataViewChromeOptions) {
  const shouldRenderTitleRow = showTitleRow && titleRow !== nothing;
  const shouldRenderToolbarRow =
    showToolbarRow && (showViewBar || showTools || trailingActions !== nothing);
  const hasToolsAfterViewBar = showTools || trailingActions !== nothing;
  const primaryRow =
    !shouldRenderTitleRow && !shouldRenderToolbarRow
      ? nothing
      : html`<div
          class="${databaseChromePrimaryRowStyles}"
          data-database-header-primary-row="true"
        >
          ${!shouldRenderTitleRow
            ? nothing
            : html`<div class="${databaseTitleRowStyles}">${titleRow}</div>`}
          ${!shouldRenderToolbarRow || !showViewBar
            ? nothing
            : html`<div
                class="${databaseViewBarContainerStyles} ${viewBarClass ?? ''}"
                data-canvas-db-zone=${surface === 'canvas'
                  ? 'toolbar-action'
                  : nothing}
                data-database-view-switcher-region="true"
                data-separated=${hasToolsAfterViewBar}
              >
                ${renderUniLit(widgetPresets.viewBar, {
                  ...props,
                  compact: true,
                  ...viewBarOptions,
                })}
              </div>`}
          ${!shouldRenderToolbarRow
            ? nothing
            : html`<div
                class="${databaseHeaderActionsStyles}"
                data-database-header-actions-surface=${surface}
              >
                ${showTools
                  ? html`<div
                      class="${databaseToolbarRowStyles} ${databaseHeaderBarStyles} ${toolbarClass ??
                      ''}"
                    >
                      ${tools}
                    </div>`
                  : nothing}
                ${trailingActions === nothing
                  ? nothing
                  : html`<div class="${databaseTrailingActionsStyles}">
                      ${trailingActions}
                    </div>`}
              </div>`}
        </div>`;
  const supplementaryContent = html`
    ${inlineBoundary} ${showQuickSettings ? quickSettings : nothing}
  `;

  if (!wrapContainer) {
    return html`${primaryRow}${supplementaryContent}`;
  }

  return html`
    <div
      class=${classMap({
        [databaseHeaderContainerStyles]: true,
        [databaseHeaderStickyStyles]: sticky,
      })}
      data-database-surface=${surface}
    >
      ${primaryRow === nothing
        ? nothing
        : html`<div
            class=${databaseHeaderAreaStyles}
            data-database-header-area="true"
          >
            ${primaryRow}
          </div>`}
      ${supplementaryContent}
    </div>
  `;
}
